import { useState, useRef, useEffect, useCallback } from "react";

export const AnnouncementMarquee = ({ announcements }) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [startX, setStartX] = useState(0);
  const [scrollStart, setScrollStart] = useState(0);
  const animationRef = useRef(null);
  const speed = 50; // pixels per second

  // Auto-scroll animation
  useEffect(() => {
    if (!contentRef.current || isDragging || isHovered) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      return;
    }

    let lastTime = performance.now();

    const animate = (currentTime) => {
      const deltaTime = (currentTime - lastTime) / 1000;
      lastTime = currentTime;

      setScrollPosition((prev) => {
        const contentWidth = contentRef.current?.scrollWidth / 2 || 0;
        let newPos = prev + speed * deltaTime;

        // Reset position when we've scrolled through half the content (duplicate)
        if (newPos >= contentWidth) {
          newPos = newPos - contentWidth;
        }
        return newPos;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isDragging, isHovered]);

  // Handle mouse/touch drag start
  const handleDragStart = useCallback((clientX) => {
    setIsDragging(true);
    setStartX(clientX);
    setScrollStart(scrollPosition);
  }, [scrollPosition]);

  // Handle mouse/touch drag move
  const handleDragMove = useCallback((clientX) => {
    if (!isDragging || !contentRef.current) return;

    const diff = startX - clientX;
    const contentWidth = contentRef.current.scrollWidth / 2;
    let newPos = scrollStart + diff;

    // Wrap around
    while (newPos < 0) newPos += contentWidth;
    while (newPos >= contentWidth) newPos -= contentWidth;

    setScrollPosition(newPos);
  }, [isDragging, startX, scrollStart]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    handleDragMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    handleDragEnd();
  };

  // Touch events
  const handleTouchStart = (e) => {
    handleDragStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleDragMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  if (!announcements || !announcements.length) {
    return null;
  }

  // Duplicate announcements for seamless loop
  const duplicatedAnnouncements = [...announcements, ...announcements];

  return (
    <div
      ref={containerRef}
      className="draggable-marquee-container"
      style={{
        overflow: "hidden",
        width: "100%",
        cursor: isDragging ? "grabbing" : "grab",
        userSelect: "none",
        WebkitUserSelect: "none",
        touchAction: "pan-y",
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        ref={contentRef}
        style={{
          display: "inline-flex",
          whiteSpace: "nowrap",
          transform: `translateX(-${scrollPosition}px)`,
          willChange: "transform",
        }}
      >
        {duplicatedAnnouncements.map((announcement, index) => (
          <AnnouncementItem key={index} title={announcement.title} />
        ))}
      </div>
    </div>
  );
};

const AnnouncementItem = ({ title }) => (
  <span style={{ marginRight: "20px", padding: "0 10px" }}>
    {title} &nbsp; * &nbsp;
  </span>
);
